import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Divider,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Typography,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { IChannel, ISaleOrderCreateForm, SegmentEnum, segmentTypes } from '../../types';
import { checkShowErrorMessage, nanoidGenerator } from 'features/common/helpers';
import { findContactDataByDeal } from 'services';
import { AutocompleteContactData, QuotationGeneralInfo } from 'features/quotation/types';
import { getQuotation, getQuotationOnlyFlights } from 'features/quotation/services';

import { ShowAlertState } from '../../../common/types';
import { useSelector } from 'react-redux';
import { getCountriesWithAgencyConfigForUser } from '../../../auth/helpers';
import { RootState } from '../../../../store';
import { createSalesOrder, getChannels } from '../../services';
import StoreIcon from '@mui/icons-material/Store';
import { extractErrorMessage } from '../../../quotation/helpers';
import BitrixDealOrCaseSwitch from '../../../common/components/BitrixDealOrCaseSwitch';
import CaseWhatsappNumberWarning from '../../../common/components/CaseWhatsappNumberWarning';
import { getNimbuxCaseSummary } from '../../../common/services';

interface IMessageInfoVerifyQuotation {
	message: string[] | string;
	success: boolean;
}

export const SalesOrderCreateForm = (): JSX.Element => {
	const history = useHistory();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { auth } = useSelector((state: RootState) => state);
	const {
		control,
		formState: { errors },
		getValues,
		setValue,
		reset,
		watch,
	} = useForm({
		mode: 'all',
		defaultValues: { segmentType: '', channelId: '', quotationId: '', dealId: '', clientReference: '' },
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [channels, setChannels] = useState<IChannel[]>([]);
	const [contactDealCheck, setContactDealCheck] = useState<IMessageInfoVerifyQuotation | null>(null);
	const [quotation, setQuotation] = useState<IMessageInfoVerifyQuotation | null>(null);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [caseClientId, setCaseClientId] = useState<string | null>(null);
	const [alert, setAlert] = React.useState<ShowAlertState>(defaultAlertState);
	const [existQuotationId, setExistQuotationId] = useState(false);
	const quotationIdValue = watch('quotationId');
	const dealIdIdValue = watch('dealId');
	const [isBitrixDeal, setIsBitrixDeal] = useState<boolean>(false);
	const countriesConfigToRender = getCountriesWithAgencyConfigForUser(auth.user?.agencies.map((x) => x.agencyId) || []);

	const convertContactDataToMessage = (contact: AutocompleteContactData): string[] => {
		const stringMessageChunk = [];
		stringMessageChunk.push(`Nombre: ${contact.name}`);
		if (contact.email) stringMessageChunk.push(`Correo: ${contact.email}`);
		if (contact.phone) stringMessageChunk.push(`Teléfono: ${contact.phone}`);
		return stringMessageChunk;
	};
	const convertQuotationIdDataMessage = (contact: QuotationGeneralInfo): string[] => {
		const stringMessageChunk = [];
		stringMessageChunk.push(`Nombre: ${contact.name}`);
		stringMessageChunk.push(`crmTicket: ${contact.crmTicket}`);
		if (contact.email) stringMessageChunk.push(`Correo: ${contact.email}`);
		if (contact.phone) stringMessageChunk.push(`Teléfono: ${contact.phone}`);
		return stringMessageChunk;
	};
	//obtengo los datos de Quotation Id
	const quotationIdData = async () => {
		const quotationID = getValues('quotationId');
		if (quotationID) {
			try {
				setLoading(true);
				const response = await getQuotation(+quotationID);
				setQuotation({ message: convertQuotationIdDataMessage(response.data), success: true });
				setExistQuotationId(true);
				setLoading(false);
			} catch (error: any) {
				try {
					const responseOnlyFlights = await getQuotationOnlyFlights(+quotationID);
					setQuotation({
						message: convertQuotationIdDataMessage(responseOnlyFlights.data),
						success: true,
					});
					setExistQuotationId(true);
					setLoading(false);
				} catch (erro: any) {
					setQuotation({ message: 'No se encontro el quotation ID', success: false });
					setExistQuotationId(false);
					setLoading(false);
				}
			}
		}
	};
	const handleQuotationId = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			quotationIdData();
		}
	};
	//obtengo los datos del Deal ID
	const findContactOfDeal = async () => {
		const crmTicket = getValues('dealId');
		if (crmTicket) {
			try {
				setLoading(true);
				const response = await findContactDataByDeal(+crmTicket);
				setContactDealCheck({ message: convertContactDataToMessage(response.data), success: true });
				setLoading(false);
			} catch (error: any) {
				if (error.response.status === 404) {
					setContactDealCheck({ message: 'El deal no tiene contacto asociado', success: true });

					setLoading(false);
				} else {
					setContactDealCheck({ message: 'No existe un deal con ese ID', success: false });
					setLoading(false);
				}
			}
		}
	};

	const findNimbuxCaseSummary = async () => {
		const crmTicket = getValues('dealId');
		if (crmTicket) {
			try {
				setLoading(true);
				setCaseClientId(null);
				const response = await getNimbuxCaseSummary(+crmTicket);
				if (!response.data?.client) {
					setContactDealCheck({
						message: 'El caso no tiene cliente asociado, por favor asocie un cliente al caso',
						success: false,
					});
				} else {
					const client = response.data.client;
					if (!client.email && !client.whatsappNumber) {
						setContactDealCheck({
							message: 'Por favor complete el email o el número de whatsaapp del cliente en el CRM de Mevuelo.',
							success: false,
						});
					} else {
						if (!client.whatsappNumber) {
							setCaseClientId(client.id);
						}
						const autocompleteContactData = {
							name: `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim(),
							email: client.email || '',
							phone: client.whatsappNumber || '',
						} as AutocompleteContactData;
						setContactDealCheck({ message: convertContactDataToMessage(autocompleteContactData), success: true });
					}
				}

				setLoading(false);
			} catch (error: any) {
				const errorMessage = extractErrorMessage(error, 'Ocurrió un error al buscar el caso');
				setContactDealCheck({ message: errorMessage, success: false });
				setLoading(false);
			}
		}
	};
	const handleDealId = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (isBitrixDeal) {
				findContactOfDeal();
			} else {
				findNimbuxCaseSummary();
			}
		}
	};

	const onSubmit = async () => {
		try {
			setLoading(true);
			const values = getValues();
			const orderToSend: ISaleOrderCreateForm = {
				dealId: Number(values.dealId),
				isBitrixDeal: isBitrixDeal,
				quotationId: values.quotationId ? Number(values.quotationId) : undefined,
				segmentType: values.segmentType as SegmentEnum,
				channelId: Number(values.channelId),
				clientReference: values.clientReference != '' ? values.clientReference : undefined,
			};
			const id = nanoidGenerator();
			await createSalesOrder(id, orderToSend);
			setAlert({
				show: true,
				severity: 'success',
				message: '¡Orden generada correctamente!',
			});
			reset();
			setContactDealCheck(null);
			setQuotation(null);
			setExistQuotationId(false);
			setLoading(false);
			history.push(`/salesorder/${id}`);
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al crear la orden'),
			});
			setLoading(false);
		}
	};

	const countryToRender = useMemo(
		() => countriesConfigToRender.find((countryConfig) => countryConfig.id === auth.country),
		[countriesConfigToRender, auth],
	);

	const isFormValid = () => {
		const isSegmentTypeValid = !errors.segmentType;
		const isDealIdValid = !errors.dealId;
		const isClientReferenceIdValid = !errors.clientReference;
		const isQuotationIdValid = !errors.quotationId;
		const isChannelIdValid = !errors.channelId;
		const isChannelIdSelected = !!getValues('channelId');
		const isSegmentTypeSelected = !!getValues('segmentType');
		return (
			isDealIdValid &&
			isSegmentTypeValid &&
			isSegmentTypeSelected &&
			isQuotationIdValid &&
			isChannelIdValid &&
			isChannelIdSelected &&
			isClientReferenceIdValid
		);
	};

	const loadChannels = async () => {
		setLoading(true);
		try {
			const response = await getChannels();
			setChannels(response.data);
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al obtener los canales'),
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadChannels();
	}, []);

	useEffect(() => {
		setValue('dealId', '');
	}, [isBitrixDeal]);

	useEffect(() => {
		setValue('channelId', '');
		setValue('quotationId', '');
		setValue('clientReference', '');
		setContactDealCheck(null);
		setQuotation(null);
		setExistQuotationId(false);
		setCaseClientId(null);
	}, [dealIdIdValue]);

	const showMessageWhatsappWarningInCase = !isBitrixDeal && caseClientId && caseClientId !== '';

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					width: '100%',
					px: isMobile ? 2 : 0,
				}}
			>
				<form
					style={{
						width: isMobile ? '100%' : '600px',
						maxWidth: isMobile ? '100%' : '600px',
					}}
				>
					<Grid container spacing={isMobile ? 3 : 2}>
						<Grid item xs={12}>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 2,
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<StoreIcon sx={{ color: 'grey.600' }} />
									<Typography variant="body1" sx={{ color: 'grey.600', fontWeight: 'bold' }}>
										Mercado
									</Typography>
								</Box>
								<Box
									display="flex"
									alignItems="center"
									justifyContent="center"
									bgcolor="#f5f5f5"
									borderRadius={2}
									sx={{
										py: 2,
										border: '1px solid #e0e0e0',
										width: '100%',
									}}
								>
									<img
										src={countryToRender?.agencyIcon}
										alt={countryToRender?.countryName}
										style={{ width: '32px', marginRight: '8px' }}
									/>
									<Typography variant="body1" sx={{ fontWeight: 'bold' }}>
										{countryToRender?.countryName}
									</Typography>
								</Box>
							</Box>
						</Grid>
						<Grid item xs={12}>
							<Divider sx={{ my: 2 }} />
						</Grid>
						<Grid item xs={12} sm={12}>
							<BitrixDealOrCaseSwitch isBitrixDeal={isBitrixDeal} onChange={() => setIsBitrixDeal(!isBitrixDeal)} />
						</Grid>
						<Grid item xs={12} sm={12}>
							<Controller
								name="dealId"
								control={control}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
									min: {
										value: 1,
										message: `El ${isBitrixDeal ? 'Deal ID' : 'Caso'} debe ser mayor a 0`,
									},
									pattern: {
										value: /^(0|[1-9]\d*)(\.\d+)?$/,
										message: 'El valor debe ser numérico y mayor que 0',
									},
								}}
								render={({ field }) => (
									<TextField
										variant="outlined"
										required={true}
										fullWidth
										type="number"
										size={isMobile ? 'medium' : 'small'}
										onWheel={(event) => {
											const element = event.target as HTMLInputElement;
											element.blur();
										}}
										label={isBitrixDeal ? 'Deal ID' : 'Caso'}
										onKeyPress={handleDealId}
										disabled={loading}
										helperText={checkShowErrorMessage(Boolean(errors.dealId), errors.dealId?.message)}
										error={Boolean(errors.dealId)}
										sx={{
											'& .MuiOutlinedInput-root': {
												height: isMobile ? '56px' : 'auto',
											},
										}}
										{...field}
										onChange={(event) => {
											field.onChange(event.target.value);
											if (contactDealCheck != null) {
												setContactDealCheck(null);
											}
										}}
									/>
								)}
							/>
							<Typography
								variant="caption"
								sx={{
									color: 'grey.600',
									mt: 1,
									display: 'block',
									fontSize: isMobile ? '0.875rem' : '0.75rem',
								}}
							>
								(ingrese el {isBitrixDeal ? 'Deal ID' : 'Caso'} y luego oprima enter)
							</Typography>
							{showMessageWhatsappWarningInCase && (
								<CaseWhatsappNumberWarning
									clientId={caseClientId || ''}
									message={
										'Importante: Ingrese el número de whatsaap del cliente del caso en el CRM de Mevuelo' +
										' si luego se desean enviar mensajes al cliente por whatsaap.'
									}
								/>
							)}
							{contactDealCheck && (
								<Typography color={contactDealCheck.success ? 'green' : 'red'} fontSize={12}>
									{' '}
									{Array.isArray(contactDealCheck.message)
										? contactDealCheck.message.map((message) => {
												return <p key={message}>{message}</p>;
										  })
										: contactDealCheck.message}{' '}
								</Typography>
							)}
						</Grid>
						<Grid item xs={12} sm={12}>
							<Controller
								name="quotationId"
								control={control}
								rules={{
									required: false,
									min: {
										value: 1,
										message: 'Quotation id debe ser mayor a 0',
									},
									pattern: {
										value: /^(0|[1-9]\d*)(\.\d+)?$/,
										message: 'El valor debe ser numérico y mayor que 0',
									},
								}}
								render={({ field }) => (
									<TextField
										type="number"
										variant="outlined"
										fullWidth
										size={isMobile ? 'medium' : 'small'}
										label="Quotation ID"
										onKeyPress={handleQuotationId}
										disabled={loading}
										helperText={checkShowErrorMessage(Boolean(errors.quotationId), errors.quotationId?.message)}
										error={Boolean(errors.quotationId)}
										sx={{
											'& .MuiOutlinedInput-root': {
												height: isMobile ? '56px' : 'auto',
											},
										}}
										{...field}
										onChange={(event) => {
											field.onChange(event.target.value);
											if (quotation != null) {
												setQuotation(null);
												setExistQuotationId(false);
											}
										}}
									/>
								)}
							/>
							<Typography
								variant="caption"
								sx={{
									color: 'grey.600',
									mt: 1,
									display: 'block',
									fontSize: isMobile ? '0.875rem' : '0.75rem',
								}}
							>
								(ingrese un numero de Quotation ID y luego oprima enter)
							</Typography>
							{quotation && (
								<Typography color={quotation.success ? 'green' : 'red'} fontSize={12}>
									{' '}
									{Array.isArray(quotation.message)
										? quotation.message.map((message) => {
												return <p key={message}>{message}</p>;
										  })
										: quotation.message}{' '}
								</Typography>
							)}
						</Grid>

						<Grid item xs={12} sm={12}>
							<Controller
								name="segmentType"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'seleccione segmento',
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl sx={{ width: '100%', height: '100%' }}>
										<InputLabel>Segmento *</InputLabel>
										<Select
											labelId="segment"
											label="segmento"
											required={true}
											disabled={loading}
											size={isMobile ? 'medium' : 'small'}
											sx={{
												width: '100%',
												'& .MuiOutlinedInput-root': {
													height: isMobile ? '56px' : 'auto',
												},
											}}
											{...field}
										>
											{segmentTypes.map((segmentType) => {
												return (
													<MenuItem key={segmentType.id} value={segmentType.id}>
														{segmentType.name}
													</MenuItem>
												);
											})}
										</Select>
										{error && <FormHelperText sx={{ color: 'red' }}>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid item xs={12} sm={12}>
							<Controller
								name="channelId"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'Seleccione un canal',
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl sx={{ width: '100%', height: '100%' }}>
										<InputLabel>Canal *</InputLabel>
										<Select
											labelId="channelId"
											label="Canal"
											required={true}
											disabled={loading}
											size={isMobile ? 'medium' : 'small'}
											sx={{
												width: '100%',
												'& .MuiOutlinedInput-root': {
													height: isMobile ? '56px' : 'auto',
												},
											}}
											{...field}
										>
											{channels.map((channel) => {
												return (
													<MenuItem key={channel.id} value={channel.id}>
														{channel.name}
													</MenuItem>
												);
											})}
										</Select>
										{error && <FormHelperText sx={{ color: 'red' }}>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid item xs={12} sm={12}>
							<Controller
								name="clientReference"
								control={control}
								rules={{
									required: false,
									maxLength: {
										value: 255,
										message: 'La referencia del cliente excede el largo máximo permitido de: 255 caracteres',
									},
								}}
								render={({ field }) => (
									<TextField
										type="text"
										variant="outlined"
										fullWidth
										size={isMobile ? 'medium' : 'small'}
										label="Referencia de cliente"
										disabled={loading}
										helperText={checkShowErrorMessage(Boolean(errors.clientReference), errors.clientReference?.message)}
										error={Boolean(errors.clientReference)}
										sx={{
											'& .MuiOutlinedInput-root': {
												height: isMobile ? '56px' : 'auto',
											},
										}}
										{...field}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12}>
							<Button
								variant="contained"
								disabled={
									loading ||
									!isFormValid() ||
									!contactDealCheck ||
									(!existQuotationId && quotationIdValue != '') ||
									(contactDealCheck && !contactDealCheck.success)
								}
								color="success"
								fullWidth
								size={isMobile ? 'large' : 'medium'}
								onClick={onSubmit}
								sx={{
									height: isMobile ? '56px' : '42px',
									fontSize: isMobile ? '1.1rem' : '0.875rem',
									fontWeight: 'bold',
									mt: isMobile ? 2 : 1,
								}}
							>
								Generar Orden
							</Button>
						</Grid>
					</Grid>
				</form>
			</Box>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
